// Body field from the server is stored as either a plain MARKDOWN string
// (the canonical format since the Crepe editor swap, 2026-08-06) or a legacy
// JSON-stringified EditorJS output object ({ time, blocks, version }). The UI
// carries content in whichever of the two shapes the row is in:
//   - markdown → the raw STRING (strings are primitives, inherently
//     signal-store leaves — no freezing needed)
//   - legacy blocks → the parsed object, FROZEN (see _docContent)
// The editor + article view normalize legacy blocks to markdown at the edge
// (components/Editor.js `_toMarkdown`); after the next save the row becomes
// markdown for good.
export const _parseBody = (raw) => {
  if (!raw) return ''
  if (typeof raw === 'object') return raw
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.blocks)) return parsed
  } catch (_) {}
  // Plain markdown/text — keep the string itself.
  return String(raw)
}

// LIST-row variant. Freezing marks a parsed LEGACY body as a LEAF value for
// the signal store (`isLeafValue` honors `Object.isFrozen`), so the keyed
// reconciler's `$replace` write of an unchanged row hits the signal's strict
// `===` dedupe instead of re-wrapping a fresh nested store per pass — the
// write-back edge of the /documents renderer hard-lock (P0). Markdown
// strings pass through untouched (primitives are already leaves). List
// content is a read-only snapshot: every writer (editor save, saveKbArticle,
// the fetch transforms) replaces the whole value, never mutates in place.
export const _docContent = (raw) => {
  const parsed = _parseBody(raw)
  return typeof parsed === 'object' ? Object.freeze(parsed) : parsed
}

// Strip markdown syntax down to preview-able plain text. Deliberately light —
// enough for card previews + search, not a parser.
// Block body with an explicit `return`, not an implicit-return chained
// arrow: frank's emission mis-parses a bare multi-line `.replace()` chain
// as the function body (surfaced as `[emitted-parse-error] Assigning to
// rvalue` on `globalScope._stripMarkdown` — the auto-lifted helper).
// Behaviour is identical. tickets/fable.md.
const _stripMarkdown = (md) => {
  return (
    String(md)
      // The Crepe serializer escapes anything that could read as syntax —
      // `\-`, `\[`, `\\`, `\.`, `\:` — and every one of them printed on the card
      // as a literal backslash ("Bullet one \- Bullet two … \[Symbols docs\]",
      // live 2026-09-04). Drop the escape, keep the character. FIRST, so an
      // escaped bracket pair then reads as the link it was and collapses to
      // its text below. A preview never needs a literal backslash, so every
      // one that survives (a run of three, one before a space) goes as well.
      .replace(/\\([^a-zA-Z0-9\s])/g, '$1')
      .replace(/\\/g, '')
      .replace(/```[\s\S]*?```/g, ' ')
      // A table's delimiter row (`| --- | :-: |`, `---|---`) and a thematic
      // break (`---`, `- - -`, `***`) are layout: the card printed them as
      // "Step Owner Status --- --- --- M0 …" (live on dev 2026-10-27). A line
      // of only dashes, pipes, colons and blanks carries no text. Blanks are
      // `[ \t]`, never `\s` (which crosses lines), and no two quantifiers
      // share a run: the first cut ran cubic on blank lines.
      .replace(/^[ \t|:]*-[ \t|:-]*$/gm, '')
      .replace(/^[ \t]*([*_])(?:[ \t]*\1){2,}[ \t]*$/gm, '')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/^\s{0,3}(#{1,6}|>+|[-*+]\s|\d+\.\s)\s*/gm, '')
      .replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, '$1')
      .replace(/<[^>]+>/g, '')
      // Table pipes are layout, not text.
      .replace(/\s*\|\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  )
}

// The note's first image, for the card thumbnail. Markdown only — a legacy
// blocks body carries images as `image` blocks the preview never showed, and
// every save rewrites the row as markdown anyway. Derived ONCE at write time
// beside `_bodyText` (same P0 renderer contract: the card factories read
// `s._cover` O(1), never scan the body per pass). Empty string, not null, so
// a row's key set stays identical whether or not it has an image — the
// identity reuse in rowIdentity.js compares key COUNTS.
export const _firstImage = (content) => {
  if (typeof content !== 'string') return ''
  // Both destination forms: a bare URL, or an angle-bracketed one (the way
  // a path with a space is written). An optional title after it is ignored.
  const m = /!\[[^\]]*\]\(\s*(?:<([^>]+)>|([^\s)]+))(?:\s+"[^"]*")?\s*\)/.exec(content)
  return m ? m[1] || m[2] : ''
}

// Plain-text preview of a body (markdown string OR legacy blocks object).
// Derived ONCE at write time and stored on the row (`_bodyText`) so
// DocsCard's Preview factories are O(1) reads — re-joining the full body
// inside the factories ran per card per reactive pass, proportional to total
// body size instead of to change.
export const _joinBodyText = (content) => {
  if (typeof content === 'string') return _stripMarkdown(content)
  return (
    (content?.blocks || [])
      .map((b) => b?.data?.text || '')
      .filter(Boolean)
      .join(' ')
      .replace(/<[^>]+>/g, '')
      // Legacy EditorJS inline text is HTML-ish — decode the common
      // entities so previews don't show a literal &nbsp;.
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
  )
}
